import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { NumberInput } from '../../ui/number-input';
import { Label } from '../../ui/label';
import type { Heat } from '../../../types/heats';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heat: Heat | null;
  loading: boolean;
  onConfirm: (minutes: number) => void;
}

/**
 * Insertion d'une pause avant un heat.
 *
 * Une pause n'est pas un objet en base : c'est un décalage. Ce heat et tous
 * ceux qui le suivent DANS LA MÊME JOURNÉE partent plus tard, et le trou
 * devient visible dans les horaires eux-mêmes. Une valeur négative resserre le
 * planning — le même geste sert donc à retirer une pause.
 */
const InsertBreakDialog: React.FC<Props> = ({ open, onOpenChange, heat, loading, onConfirm }) => {
  const { t } = useTranslation();
  const [minutes, setMinutes] = React.useState(10);

  // Repartir du défaut à chaque ouverture : garder la dernière valeur saisie
  // ferait appliquer en silence un décalage qu'on ne relit pas.
  React.useEffect(() => { if (open) setMinutes(10); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('heats.insertBreak.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            {t('heats.insertBreak.description', {
              heat: heat?.heat_number ?? '',
              time: heat?.start_time?.substring(0, 5) ?? '',
            })}
          </p>

          <div className="space-y-2">
            <Label>{t('heats.insertBreak.minutes')}</Label>
            <NumberInput
              value={minutes}
              onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">{t('heats.insertBreak.hint')}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onConfirm(minutes)} disabled={loading || minutes === 0}>
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            {t('heats.insertBreak.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InsertBreakDialog;
